import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { collectionColors, font, formatFileSize, isYamlFile, subtitleSx } from "./tokens";

interface FileDropzoneProps {
  file: File | null;
  onSelect: (file: File | null) => void;
  /** Idle text, e.g. "Upload primary .yaml / .yml". */
  placeholder?: string;
  /** Compact height for the inline variant slots inside the create modal. */
  compact?: boolean;
  /** `accept` for the file input. Defaults to YAML, which every collection file was. */
  accept?: string;
  /** Rejects a file the caller cannot use. Defaults to the YAML check. */
  validate?: (file: File) => boolean;
  /** Small print under the placeholder, naming the accepted formats. */
  caption?: string;
}

/**
 * File dropzone shared by every collection modal.
 * Extracted from the Surveys tab, where the same markup was duplicated per modal.
 *
 * Defaults to YAML — every collection file was one until reports arrived — and takes
 * `accept`/`validate`/`caption` for anything else.
 */
const FileDropzone: React.FC<FileDropzoneProps> = ({
  file,
  onSelect,
  placeholder = "Click to upload or drag and drop",
  compact = false,
  accept = ".yaml,.yml",
  validate = isYamlFile,
  caption = "YAML files only (.yaml / .yml)",
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const [rejected, setRejected] = useState<string | null>(null);

  const stop = (event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  };

  /**
   * Runs on both paths. The picker used to trust `accept`, but that attribute only
   * filters the browser's dialog — "All files" is one click away, and a drop skipped it
   * entirely — so the wrong file used to reach the modal silently.
   */
  const accepting = (candidate: File) => {
    if (validate(candidate)) {
      setRejected(null);
      onSelect(candidate);
      return;
    }
    setRejected(`"${candidate.name}" is not an accepted format.`);
  };

  if (file) {
    return (
      <Box
        sx={{
          boxSizing: "border-box",
          display: "flex",
          alignItems: "center",
          gap: "10px",
          width: "100%",
          minHeight: compact ? "44px" : "56px",
          padding: "8px 12px",
          background: collectionColors.surfaceLight,
          border: `1px solid ${collectionColors.border}`,
          borderRadius: "10px",
        }}
      >
        <DescriptionOutlinedIcon sx={{ width: 16, height: 16, color: collectionColors.textSecondary }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: font,
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "20px",
              color: collectionColors.textPrimary,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {file.name}
          </Typography>
          <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
            {formatFileSize(file.size)}
          </Typography>
        </Box>
        <Box
          component="button"
          type="button"
          aria-label="Remove file"
          onClick={() => onSelect(null)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "24px",
            height: "24px",
            background: "transparent",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            "&:hover": { background: "rgba(0, 0, 0, 0.05)" },
          }}
        >
          <CloseIcon sx={{ width: 14, height: 14, color: collectionColors.textSecondary }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", width: "100%" }}>
    <Box
      component="label"
      onDragOver={stop}
      onDragEnter={(event: React.DragEvent) => {
        stop(event);
        setIsDragging(true);
      }}
      onDragLeave={(event: React.DragEvent) => {
        stop(event);
        setIsDragging(false);
      }}
      onDrop={(event: React.DragEvent) => {
        stop(event);
        setIsDragging(false);
        const dropped = event.dataTransfer.files?.[0];
        if (dropped) {
          accepting(dropped);
        }
      }}
      sx={{
        boxSizing: "border-box",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: compact ? "4px" : "8px",
        width: "100%",
        height: compact ? "56px" : "148px",
        border: `2px dashed ${isDragging ? collectionColors.dropzoneActive : collectionColors.border}`,
        borderRadius: "10px",
        cursor: "pointer",
        background: isDragging ? collectionColors.dropzoneActiveSurface : "transparent",
        "&:hover": {
          borderColor: isDragging ? collectionColors.dropzoneActive : "rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      <input
        type="file"
        accept={accept}
        style={{ display: "none" }}
        onChange={(event) => {
          const selected = event.target.files?.[0];
          if (selected) {
            accepting(selected);
          }
          // Cleared so picking the same file twice still fires onChange — otherwise a
          // rejected file cannot be re-picked after the user renames it.
          event.target.value = "";
        }}
      />
      {!compact && (
        <Box sx={{ width: "40px", height: "40px", color: collectionColors.textSecondary }}>
          <svg width="40" height="40" viewBox="0 0 40 40" fill="none">
            <path
              d="M5 25V30C5 31.3261 5.52678 32.5979 6.46447 33.5355C7.40215 34.4732 8.67392 35 10 35H30C31.3261 35 32.5979 34.4732 33.5355 33.5355C34.4732 32.5979 35 31.3261 35 30V25"
              stroke="currentColor"
              strokeWidth="3.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M11.6667 13.3333L20 5L28.3333 13.3333"
              stroke="currentColor"
              strokeWidth="3.33333"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path d="M20 5V25" stroke="currentColor" strokeWidth="3.33333" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Box>
      )}
      <Typography
        sx={{
          fontFamily: font,
          fontWeight: 500,
          fontSize: "14px",
          lineHeight: "20px",
          letterSpacing: "-0.150391px",
          color: collectionColors.textSecondary,
        }}
      >
        {placeholder}
      </Typography>
      {!compact && (
        <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
          {caption}
        </Typography>
      )}
    </Box>
      {/* Inline, not a toast: the toast is one truncated line and the message names a
          file, which the user needs to read next to the slot they dropped it on. */}
      {rejected && (
        <Typography
          sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px", color: collectionColors.danger }}
        >
          {rejected} {caption}
        </Typography>
      )}
    </Box>
  );
};

export default FileDropzone;
